import { Chatv1Error, type GameSide, type GameView } from '../chatv1/transport'
import { kindOf } from './gameKinds'

/** A refusal from /games/* in words (chat lambda/games.ts names them). */
const SAID: Record<string, string> = {
  not_your_turn: 'It is not your move.',
  illegal_move: 'That move is not allowed here.',
  invalid_move: 'That move is not allowed here.',
  game_over: 'This game is over.',
  moved_meanwhile: 'The board changed meanwhile — try again.',
  no_such_game: 'That game is not here any more.',
  not_your_agent: 'Pick one of your own agents.',
  invalid_agent: 'Pick one of your own agents.',
  agent_offline: 'That agent’s machine is offline right now.',
  agent_unreachable: 'That agent could not be reached — try again in a moment.',
  asked_recently: 'Your agent was just asked — give it a moment.',
  not_agents_turn: 'It is not your agent’s move.',
  no_token: 'Sign in again to play.',
  not_a_friend: 'You can invite only your friends.',
  not_started: 'The game starts when your friend accepts.',
  your_own_invitation: 'Your friend has to accept it.',
  already_started: 'This game has already started.',
  empty_message: 'Write something first.',
  message_too_long: 'That message is too long (500 characters at most).',
  nobody_to_tell: 'There is nobody to send a message to in this game.',
  // Mahjong (chat mahjongTable.ts).
  you_left: 'You left this table — the computer plays your seat.',
  agent_is_playing: 'That agent plays a seat at this table — ask another one.',
  not_your_invitation: 'Only whoever sent the invitation can start the game.',
  too_many_seats: 'A table has four seats — pick fewer friends and agents.',
  deal_not_over: 'This deal is still being played.',
  not_in_hand: 'That tile is not in your hand any more.',
  not_a_win: 'Your hand is not complete.',
  no_such_kong: 'That kong is not open to you now.',
  nothing_to_claim: 'That discard has been taken or passed.',
  not_yours_to_claim: 'That discard has been taken or passed.',
  not_allowed: 'That play is not open to you now.',
}

export function gameErrorText(e: unknown): string {
  if (e instanceof Chatv1Error) return SAID[e.code] ?? `Something went wrong (${e.code}).`
  return e instanceof Error ? e.message : String(e)
}

const REASON: Record<string, string> = {
  checkmate: 'checkmate',
  stalemate: 'stalemate',
  repetition: 'the same position three times',
  fifty_moves: 'fifty moves without a capture or a pawn move',
  insufficient: 'not enough pieces left to mate',
  five: 'five in a row',
  board_full: 'a full board',
  perpetual_check: 'perpetual check',
  sixty_moves: 'sixty moves without a capture',
  move_limit: 'the move limit',
  resigned: 'the other side leaving',
  abandoned: 'the other side leaving',
}

/** How it ended, from the viewer's side: "You won by checkmate", "Draw by stalemate". */
export function outcomeText(g: GameView): string {
  if (g.status !== 'over') return ''
  const them: GameSide = g.you === 'w' ? 'b' : 'w'
  if (g.reason === 'declined') return g.invited === 'you' ? `${seatName(g, them)} declined the game` : 'You declined the game'
  if (g.reason === 'cancelled') return g.invited === 'you' ? 'You took the invitation back' : `${seatName(g, them)} took the invitation back`
  if (!g.result) return ''
  if (g.reason === 'resigned' && g.you) {
    const winner: GameSide = g.result === '1-0' ? 'w' : 'b'
    return g.you === winner ? `${seatName(g, them)} left the game — you win` : 'You left the game'
  }
  const why = REASON[g.reason ?? ''] ?? 'the rules'
  if (g.result === '1/2-1/2') return `Draw by ${why}`
  const winner: GameSide = g.result === '1-0' ? 'w' : 'b'
  if (g.you) return g.you === winner ? `You won by ${why}` : `You lost by ${why}`
  return `${kindOf(g.type).sides[winner]} won by ${why}`
}

export function seatName(g: GameView, s: GameSide): string {
  const p = g.players[s]
  return p.name || (p.kind === 'agent' ? 'Agent' : 'Someone')
}

/** One line under the board: whose move, what is awaited, or how it ended. */
export function statusText(g: GameView): string {
  if (g.status === 'over') return outcomeText(g)
  const them: GameSide = g.you === 'w' ? 'b' : 'w'
  if (g.status === 'invited') {
    return g.invited === 'you' ? `Waiting for ${seatName(g, them)} to accept` : `${seatName(g, them)} invites you to a game`
  }
  if (g.turn === g.you) return g.check ? 'Check — your move' : 'Your move'
  const p = g.players[them]
  if (p.kind === 'agent') {
    if (g.agent_problem === 'offline') return `${p.name}’s machine is offline`
    if (g.agent_problem === 'unreachable') return `${p.name} could not be reached`
    if (g.agent_problem === 'illegal' && !g.agent_thinking) return `${p.name} did not answer with a legal move`
    return `${p.name} is thinking…`
  }
  return `Waiting for ${seatName(g, them)}`
}

/** "1. e4 e5 2. Nf3" as rows of [number, first side's move, second's] — White's first in chess, Black's in gomoku, Red's in xiangqi. */
export function moveRows(moves: readonly string[]): Array<[number, string, string]> {
  const rows: Array<[number, string, string]> = []
  for (let i = 0; i < moves.length; i += 2) rows.push([i / 2 + 1, moves[i]!, moves[i + 1] ?? ''])
  return rows
}

/**
 * What to tell a person about the OTHER player's act in a game they are not looking at — an invitation, an answer,
 * the other leaving, a message — or null (their own act, or one not worth interrupting for: a move).
 */
export function noticeFor(g: GameView, event: { kind: string; mine: boolean } | undefined): string | null {
  if (!event || event.mine || !g.you) return null
  const them: GameSide = g.you === 'w' ? 'b' : 'w'
  const who = seatName(g, them)
  const noun = kindOf(g.type).noun
  switch (event.kind) {
    case 'invited':
      return `${who} invites you to a game of ${noun}`
    case 'accepted':
      return `${who} accepted — the game is on`
    case 'declined':
      return `${who} declined your game of ${noun}`
    case 'cancelled':
      return `${who} took back the invitation to play ${noun}`
    case 'left':
      return `${who} left the game — you win`
    case 'said': {
      const last = g.chat[g.chat.length - 1]
      return last ? `${who}: ${last.text}` : null
    }
    default:
      return null
  }
}
